// Translations. FR / DE / EN / IT, matching the marketing site's language set.
//
// Flat dot-keys with `{placeholders}`; a missing key falls back to English and, in dev,
// logs once so gaps surface during development rather than in front of a customer.

import { fr } from "./fr.js";
import { de } from "./de.js";
import { en } from "./en.js";
import { it } from "./it.js";

export const DICTS = { fr, de, en, it };
export const LANGS = [
  { code: "fr", label: "FR", name: "Français" },
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "en", label: "EN", name: "English" },
  { code: "it", label: "IT", name: "Italiano" },
];

// Country → default language. Luxembourg is trilingual; French is the safest default
// for a car purchase, and the picker is one click away.
export const COUNTRY_LANG = { CH: "de", FR: "fr", DE: "de", LU: "fr" };

export const COUNTRIES = [
  { code: "CH", flag: "🇨🇭", currency: "CHF", name: { fr: "Suisse", de: "Schweiz", en: "Switzerland", it: "Svizzera" } },
  { code: "FR", flag: "🇫🇷", currency: "EUR", name: { fr: "France", de: "Frankreich", en: "France", it: "Francia" } },
  { code: "DE", flag: "🇩🇪", currency: "EUR", name: { fr: "Allemagne", de: "Deutschland", en: "Germany", it: "Germania" } },
  { code: "LU", flag: "🇱🇺", currency: "EUR", name: { fr: "Luxembourg", de: "Luxemburg", en: "Luxembourg", it: "Lussemburgo" } },
];

const warned = new Set();

export function translate(lang, key, vars) {
  const dict = DICTS[lang] || DICTS.en;
  let s = dict[key] ?? DICTS.en[key];
  if (s == null) {
    if (import.meta.env?.DEV && !warned.has(key)) { warned.add(key); console.warn(`[i18n] missing key: ${key}`); }
    return key;
  }
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, v);
  return s;
}

/**
 * A stored spec value — "Petrol", "Semi-automatic", "Full-size" — in the reader's language.
 *
 * The catalogue stores one canonical English vocabulary so that filters, facets and search
 * work the same across Korea and Japan. That left a French page reading "Petrol · Automatic"
 * under a French headline. Anything outside the closed vocabulary comes back untouched and
 * without a dev warning: an unmapped value here is a manufacturer's own name, not a gap.
 */
export function specLabel(lang, kind, value) {
  if (value == null || value === "") return "";
  const slug = String(value)
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")   // Coupé → coupe, not coup-
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const key = `spec.${kind}.${slug}`;
  return DICTS[lang]?.[key] ?? DICTS.en[key] ?? String(value);
}

/** Pick the right string out of a `{en, fr, de}` object coming from the API. */
export function pickLabel(label, lang) {
  if (label == null) return "";
  if (typeof label === "string") return label;
  return label[lang] ?? label.en ?? label.fr ?? label.de ?? Object.values(label)[0] ?? "";
}
